/**
 * 018: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/form-group.tsx (the lookup form)
 * + vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-details.tsx (the record's
 *   label:value grid) — a lookup, not a list (see screen-map §9). `transaction-card.tsx` was cited
 *   here before 018 without ever being rendered — corrected, since the real barrel `TransactionCard`
 *   is a stat card (icon + title + amount + up/down %) and does not fit a multi-fact payment record.
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useEffect, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { PiArrowRightBold as ArrowRight } from "react-icons/pi";
import { Badge, Flex, Text } from "rizzui";
import { can } from "../../lib/permissions";
import { getPayment, money, paymentStatusColor, readPayment, resolutionSource, type Payment } from "../../lib/services/finance";
import { ApiError } from "../../lib/api";
import type { AccountSummary, PermissionState } from "../../types";
import { formatMoment } from "../../lib/format";
import { PageHeader, WidgetCard, ErrorSurface, Field, FormGroup } from "@steelyard/theme";
import FormFooter from "@core/components/form-footer";
import { orderPath } from "../../lib/paths";

/**
 * Ported from apps/admin/app/(app)/finance/transactions/page.tsx. A lookup by identifier, kept
 * alongside the FIN-05 list (../Payments.tsx) rather than folded into it: a refund row's own
 * `paymentId` (and this screen's own "Refunds against this payment" link) still needs somewhere to
 * land on a single record without a list's paging/filters around it.
 */
export default function TransactionsPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("paymentId")?.trim();

  const [payment, setPayment] = useState<Payment | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [inputError, setInputError] = useState<string>();
  const [resets, setResets] = useState(0);

  useEffect(() => {
    if (!id) {
      setPayment(null);
      setError(null);
      return;
    }
    setPayment(null);
    setError(null);
    getPayment(id).then(setPayment, setError);
  }, [id]);

  if (!can(permissionState, "billing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view gateway transactions" />;
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const typed = String(new FormData(event.currentTarget).get("paymentId") ?? "").trim();
    if (!typed) return setInputError("Enter a payment identifier.");
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(typed)) {
      return setInputError("A payment identifier looks like 01a02427-1980-77e2-b0ea-4b370048b75a.");
    }
    setInputError(undefined);
    setSearchParams({ paymentId: typed });
  }

  return (
    <div className="flex flex-col gap-6">
            <>
        <PageHeader
          title="Gateway Transactions"
          breadcrumb={[{ name: "Home", href: "/" }, { name: "Finance" }, { name: "Gateway Transactions" }]}
        >
          <>
            <Link to="/finance/payments" className="text-primary inline-flex items-center gap-1 hover:underline">
              All payments
              <ArrowRight aria-hidden className="size-3.5" />
            </Link>
            <Link to="/finance/refunds" className="text-primary inline-flex items-center gap-1 hover:underline">
              Outstanding refunds
              <ArrowRight aria-hidden className="size-3.5" />
            </Link>
          </>
        </PageHeader>
        <Text className="mb-6 max-w-2xl text-gray-500">One payment at a time, with the reference that matches it to a row on the provider&apos;s own statement.</Text>
      </>

      {/* Keyed on the URL's id so Clear (and back/forward) resets the uncontrolled field too. */}
      <form key={`${id ?? ""}:${resets}`} onSubmit={handleSubmit} noValidate>
        <FormGroup title="Find a payment" description="By its identifier. Refund rows carry one, so a refund you are working is one click from the payment behind it.">
          <Field label="Payment identifier" required name="paymentId" defaultValue="" placeholder="01a02427-1980-77e2-b0ea-4b370048b75a" className="font-mono" error={inputError} />
        </FormGroup>
        <FormFooter submitBtnText="Look it up" altBtnText="Clear" handleAltBtn={() => {
            setInputError(undefined);
            setResets((n) => n + 1);
            setSearchParams({});
          }} className="static mt-6" />
      </form>

      {error ? <ErrorSurface error={error instanceof ApiError ? error : { title: "Billing returned no payment for that identifier" }} action="load that payment" /> : null}

      {payment ? <PaymentRecord payment={payment} /> : null}
    </div>
  );
}

function PaymentRecord({ payment }: { payment: Payment }) {
  const reading = readPayment(payment.status);

  return (
    <>
      <WidgetCard
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{payment.providerReference}</span>
            <Badge variant="flat" color={paymentStatusColor(payment.status)} rounded="md">
              {reading.heading}
            </Badge>
          </span>
        }
        headerClassName="border-muted border-b"
        description={reading.meaning}
      >
        <div className="grid gap-4 pt-4 sm:grid-cols-2">
          <div>
            <Text className="mb-2 text-sm font-semibold">Provider</Text>
            <Text className="font-mono text-sm break-all">{payment.provider}</Text>
          </div>
          <div>
            <Text className="mb-2 text-sm font-semibold">Provider reference</Text>
            <Text className="font-mono text-sm break-all">{payment.providerReference}</Text>
          </div>
          <div>
            <Text className="mb-2 text-sm font-semibold">Status, as the service spells it</Text>
            <Text className="font-mono text-sm break-all">{payment.status}</Text>
          </div>
          <div>
            <Text className="mb-2 text-sm font-semibold">Amount</Text>
            <Text>{money(payment.amount)}</Text>
          </div>
          <div>
            <Text className="mb-2 text-sm font-semibold">Refunded so far</Text>
            <Text>{money(payment.refundedTotal)}</Text>
          </div>
          <div>
            <Text className="mb-2 text-sm font-semibold">Still refundable</Text>
            <Text>{money(payment.refundableRemaining)}</Text>
          </div>
          <div>
            <Text className="mb-2 text-sm font-semibold">Resolved at</Text>
            <Text className="font-mono text-sm break-all">{payment.resolvedAt ? formatMoment(payment.resolvedAt) : "not resolved"}</Text>
          </div>
        </div>

        <Flex gap="6" className="mt-5 border-t border-muted pt-4 text-sm">
          <Link to={`/finance/refunds?paymentId=${encodeURIComponent(payment.paymentId)}`} className="text-primary font-medium hover:underline">
            Refunds against this payment
          </Link>
          <Link to={orderPath(payment.orderNumber || payment.orderId)} className="text-primary font-medium hover:underline">
            {payment.orderNumber ? `Order ${payment.orderNumber}` : "Open the order"}
          </Link>
        </Flex>
      </WidgetCard>

      <WidgetCard title="How this payment was resolved" headerClassName="border-muted border-b" description={<>Resolved by {resolutionSource(payment.resolvedBy)}.</>} />
    </>
  );
}
